import * as React from 'react';

import {LegacySetupTableEmptyState} from 'shells/table/legacy-setup-table-empty-state';
import {LegacySetupTableWrapper} from 'shells/table/legacy-setup-table-wrapper';
import {SetupItemTable} from 'shells/table/setup-item-table';
import type {Row} from 'shells/table/types';

import type {
    ProductCategoryCreateInput,
    ProductCategoryUpdateInput,
    ProductCategoryDeleteInput,
    GetProductsCategories_productCategories as ProductCategory,
} from 'nutshell-graphql-types';

import {BASE_COLUMNS} from './consts';
import {mapCategoriesToTableItems} from './helpers';
import {ProductCategoryRowActionMenu} from './product-categories-row-action-menu';
import {CategoryCreateOrUpdatePopover} from './product-category-create-update-popover';
import {CategoryDeletePopover} from './product-category-delete-popover';

type Props = {
    productCategories: Array<ProductCategory>;
    onProductCategoryCreate: (arg1: ProductCategoryCreateInput) => Promise<any>;
    onProductCategoryUpdate: (arg1: ProductCategoryUpdateInput) => Promise<any>;
    onProductCategoryDelete: (arg1: ProductCategoryDeleteInput) => Promise<any>;
    isSaving: boolean;
    errorSaving: string | null | undefined;
    setErrorSaving: (error?: string | null | undefined) => void;
};

export function ProductCategoriesTable(props: Props) {
    const [isAddingProductCategory, setIsAddingProductCategory] = React.useState<boolean>(false);
    const [isEditingProductCategory, setIsEditingProductCategory] = React.useState<boolean>(false);
    const [isDeletingProductCategory, setIsDeletingProductCategory] =
        React.useState<boolean>(false);
    const [selectedProductCategory, setSelectedProductCategory] =
        React.useState<ProductCategory | null>(null);

    const categoryRef = React.useRef<HTMLDivElement | null | undefined>(null);

    const handleProductCategoryEdit = (productCategory: ProductCategory) => {
        props.setErrorSaving(null);
        setIsEditingProductCategory(true);
        setSelectedProductCategory(productCategory);
    };

    const handleProductCategoryDelete = (productCategory: ProductCategory) => {
        props.setErrorSaving(null);
        setIsDeletingProductCategory(true);
        setSelectedProductCategory(productCategory);
    };

    const renderRowActionMenu = (row: Row<any>, rowElement?: HTMLDivElement | null) => {
        const productCategory: ProductCategory = row.original;

        return (
            <ProductCategoryRowActionMenu
                onEditProductCategory={() => {
                    if (rowElement) {
                        categoryRef.current = rowElement;
                    }
                    handleProductCategoryEdit(productCategory);
                }}
                onDeleteProductCategory={() => {
                    if (rowElement) {
                        categoryRef.current = rowElement;
                    }
                    handleProductCategoryDelete(productCategory);
                }}
                reportLink={productCategory.reportLink}
            />
        );
    };

    const handleModalClose = () => {
        setIsAddingProductCategory(false);
        setIsEditingProductCategory(false);
        setIsDeletingProductCategory(false);
        setSelectedProductCategory(null);

        // clear refs
        categoryRef.current = null;
    };

    return (
        <>
            <LegacySetupTableWrapper title='Product categories'>
                <SetupItemTable
                    items={mapCategoriesToTableItems(props.productCategories)}
                    title='Name'
                    error={null}
                    isLoading={false}
                    emptyState={<LegacySetupTableEmptyState text='No categories found' />}
                    onAddRowClick={() => {
                        props.setErrorSaving(null);
                        setIsAddingProductCategory(true);
                    }}
                    addButtonText='Add category'
                    renderRowActionMenu={renderRowActionMenu}
                    additionalColumns={BASE_COLUMNS}
                    includeLastUsedTimeCol={false}
                    sortBy={[{id: 'name', desc: false}]}
                    getRowRef={(ref) => {
                        categoryRef.current = ref || null;
                    }}
                    getAddButtonRef={(ref) => {
                        categoryRef.current = ref || null;
                    }}
                />
            </LegacySetupTableWrapper>
            {isAddingProductCategory && (
                <CategoryCreateOrUpdatePopover
                    variant='create'
                    onSubmit={props.onProductCategoryCreate}
                    onClose={handleModalClose}
                    isSaving={props.isSaving}
                    anchor={categoryRef.current}
                    errorSaving={props.errorSaving}
                />
            )}
            {isEditingProductCategory && selectedProductCategory && (
                <CategoryCreateOrUpdatePopover
                    variant='update'
                    productCategory={selectedProductCategory}
                    onSubmit={props.onProductCategoryUpdate}
                    onClose={handleModalClose}
                    isSaving={props.isSaving}
                    anchor={categoryRef.current}
                    errorSaving={props.errorSaving}
                />
            )}
            {isDeletingProductCategory && selectedProductCategory && (
                <CategoryDeletePopover
                    productCategory={selectedProductCategory}
                    onSubmit={props.onProductCategoryDelete}
                    onClose={handleModalClose}
                    anchor={categoryRef.current}
                    errorSaving={props.errorSaving}
                />
            )}
        </>
    );
}
